import React, { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/pages/crm/LeadForm";

export const ProfitSettings = ({ config, onSaved }) => {
  const [form, setForm] = useState({
    working_days: 26, hours_per_day: 8, freight_basis: "value",
    revenue_basis: "net", thin_margin_pct: 15,
  });
  const [staff, setStaff] = useState([]);
  const [salaries, setSalaries] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (config) setForm((f) => ({ ...f, ...config })); }, [config]);
  useEffect(() => {
    api.get("/profit/salaries").then((r) => {
      setStaff(r.data);
      setSalaries(Object.fromEntries(r.data.map((u) => [u.id, u.monthly_salary || 0])));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/profit/config", {
        working_days: Number(form.working_days), hours_per_day: Number(form.hours_per_day),
        freight_basis: form.freight_basis, revenue_basis: form.revenue_basis,
        thin_margin_pct: Number(form.thin_margin_pct),
      });
      await api.post("/profit/salaries", {
        salaries: Object.fromEntries(Object.entries(salaries).map(([k, v]) => [k, Number(v) || 0])),
      });
      toast.success("Saved");
      onSaved?.(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const total = Object.values(salaries).reduce((a, v) => a + Number(v || 0), 0);

  return (
    <div className="space-y-5" data-testid="profit-settings">
      <div className="rounded-xl border border-border bg-card p-4 shadow-xs">
        <p className="font-display text-sm font-semibold">How the numbers are worked out</p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <Field label="Working days in a month" hint="Used for the salary day rate.">
            <Input type="number" min="1" max="31" value={form.working_days} data-testid="ps-working-days"
              onChange={(e) => setForm({ ...form, working_days: e.target.value })} />
          </Field>
          <Field label="Working hours in a day" hint="Turns processing and repair hours into days.">
            <Input type="number" min="1" max="24" value={form.hours_per_day} data-testid="ps-hours"
              onChange={(e) => setForm({ ...form, hours_per_day: e.target.value })} />
          </Field>
          <Field label="Spread freight by">
            <Select value={form.freight_basis} onValueChange={(v) => setForm({ ...form, freight_basis: v })}>
              <SelectTrigger data-testid="ps-freight-basis"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="value">Product value</SelectItem>
                <SelectItem value="qty">Quantity received</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Revenue counted as">
            <Select value={form.revenue_basis} onValueChange={(v) => setForm({ ...form, revenue_basis: v })}>
              <SelectTrigger data-testid="ps-revenue-basis"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="net">Net of GST (recommended)</SelectItem>
                <SelectItem value="gross">Invoice total with GST</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Warn below this margin %" hint="Invoices under this show up as thin margin.">
            <Input type="number" min="0" max="100" value={form.thin_margin_pct} data-testid="ps-thin-margin"
              onChange={(e) => setForm({ ...form, thin_margin_pct: e.target.value })} />
          </Field>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 shadow-xs">
        <p className="font-display text-sm font-semibold">Monthly salaries</p>
        <p className="text-xs text-muted-foreground">
          Only used to work out employee cost per invoice. Days spent at a customer are charged to that
          customer first, and the rest of the month's salary is divided across the invoices that person raised.
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {staff.map((u) => (
            <Field key={u.id} label={`${u.name} (${u.designation || u.role})`}>
              <Input type="number" min="0" value={salaries[u.id] ?? 0} data-testid={`ps-salary-${u.id}`}
                onChange={(e) => setSalaries({ ...salaries, [u.id]: e.target.value })} />
            </Field>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Total monthly salary cost on record: <b className="num">{money(total)}</b>
        </p>
      </div>

      <Button onClick={save} disabled={busy} data-testid="ps-save">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
      </Button>
    </div>
  );
};
